import { HttpErrorResponse } from '@angular/common/http';
import { ApplicationRef, ErrorHandler, Injectable, Injector } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { environment } from 'environments/environment';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { filter, take } from 'rxjs/operators';

import { InstrumentationService } from './services/instrumentation.service';
import { LayoutService } from './services/layout.service';
import { ModalService } from './services/modal.service';

@Injectable()
export class GlobalErrorHandler extends ErrorHandler {
  private currentNavigationError?: NavigationError;

  constructor(
    private injector: Injector,
    private layoutService: LayoutService,
  ) {
    super();
  }

  init() {
    const router = this.injector.get(Router);

    router.events
      .pipe(filter((e): e is NavigationError => e instanceof NavigationError))
      .subscribe(next => (this.currentNavigationError = next));
  }

  handleError(e: any) {
    this.injector.get(LayoutService).checkForOutage();

    if (this.tryHandleCurrentNavigationError(e)) {
      return;
    }

    if (!environment.production) {
      // When App Insights is disabled locally, log to the console for developer debugging
      console.log(e);
    }

    if (!this.isHttpError(e)) {
      // Send error
      const instrumentation = this.injector.get(InstrumentationService);
      instrumentation.logError(e);
    } else {
      const appRef = this.injector.get(ApplicationRef);
      const toastr = this.injector.get(ToastrService);

      const toast = this.getErrorToast(e, toastr);

      toast.onShown.pipe(take(1)).subscribe(_ => appRef.tick());
    }

    super.handleError(e);
  }

  getErrorToast(e: HttpErrorResponse, toastr: ToastrService): ActiveToast<any> {
    if (e.status >= 500) {
      return toastr.error(
        'If this error recurs, please contact your administrator with details of what you were attempting to do at the time along with any screenshots or attachments that you consider to be relevant.',
        'We detected an error while processing your request.',
      );
    }

    if (e.status === 402) {
      this.layoutService.setPaymentRequired();
      return toastr.error('Payment required.');
    }

    if (e.status === 403) {
      // TODO: create a modal?
      return toastr.error('Access to the requested resource is denied.');
    }

    const error = e.error as
      | string
      | Array<{ message: string }>
      | { message: string }
      | { errors: string[] }
      | Event
      | Record<string, string>
      | undefined;

    if (error && !(error instanceof Event)) {
      if (typeof error === 'string') {
        const errorMessage = this.formatErrorDictionary(error);
        return toastr.error(errorMessage);
      }

      if ('message' in error) {
        return toastr.error(error.message);
      }

      if ('errors' in error) {
        if (Object.values(error.errors).length) {
          const firstError = Object.values(error.errors)[0];
          return toastr.error(Array.isArray(firstError) ? firstError[0] : firstError);
        }
      } else if (Object.values(error).length) {
        const firstError = Object.values(error)[0];
        return toastr.error(firstError?.message ?? firstError);
      }
    }

    if (e.message) {
      return toastr.error('Something went wrong.');
    }

    return toastr.error('Failed to validate your request.');
  }

  formatErrorDictionary(errors: string): string {
    try {
      const errorMessages: Record<string, string> = JSON.parse(errors);

      const errorValues = Object.values(errorMessages);
      if (errorValues.length) {
        return errorValues[0];
      }

      return errors;
    } catch (e) {
      return errors;
    }
  }

  isHttpError(e: any): e is HttpErrorResponse {
    return e && (e.status || e.ok === false);
  }

  tryHandleCurrentNavigationError(errorObj: Error) {
    if (
      !this.currentNavigationError ||
      !/Loading chunk/.test(errorObj.message) ||
      !/Loading chunk/.test(this.currentNavigationError.error.message)
    ) {
      return false;
    }

    const { url } = this.currentNavigationError;

    const modalService = this.injector.get(ModalService);

    modalService
      .showQuestionModal(
        '<p>This could be caused by an update to FinQuery Fixed Assets, or as a result of a network issue.</p>' +
          '<p>Would you like to retry loading that page?</p>',
        'Hmm, there was an issue loading that page',
      )
      .subscribe(() => {
        location.href = url;
      });
    return true;
  }
}
